/**
 * learningJourneyService.js
 * Tracks every meaningful learning action and stores it in:
 *   1. Firestore `journeyEvents` — for fast querying and real-time dashboard
 *   2. Google Drive `Ekamanam/Progress/` — as a daily JSON archive
 *
 * Also provides AI-powered Bloom's Taxonomy classification and Insights generation.
 */

import { db } from '../firebase/config';
import {
  collection, addDoc, getDocs, query,
  where, orderBy, limit, Timestamp, serverTimestamp,
} from 'firebase/firestore';
import {
  initializeFolderStructure, uploadFile, hasDrivePermissions,
} from './googleDriveService';
import { callLLM } from './llmService';

// ─────────────────────────────────────────────
// 1. EVENT LOGGING
// ─────────────────────────────────────────────

/**
 * Log a learning event to Firestore + Drive (fire-and-forget — never blocks UI).
 *
 * @param {string} userId
 * @param {'hub_visit'|'pdf_open'|'ai_query'|'tool_open'} type
 * @param {object} data  — contextual fields (hubId, hubName, pdfId, mode, queryText, etc.)
 */
export const logJourneyEvent = (userId, type, data = {}) => {
  if (!userId) return;
  const today = new Date().toISOString().slice(0, 10);
  const isoNow = new Date().toISOString();

  // Strip undefined values — Firestore rejects them (e.g. pdfId when no PDF is selected)
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined)
  );

  // 1. Firestore (instant, indexed for queries)
  addDoc(collection(db, 'journeyEvents'), {
    userId, type, timestamp: serverTimestamp(), date: today, ...cleanData,
  }).catch(() => {});

  // 2. Drive daily archive (async, non-blocking)
  _appendEventToDrive(userId, today, { ...cleanData, type, date: today, timestamp: isoNow })
    .catch(() => {});
};

// ─────────────────────────────────────────────
// 2. DRIVE PERSISTENCE
// ─────────────────────────────────────────────

async function _appendEventToDrive(userId, date, event) {
  if (!hasDrivePermissions()) return;

  try {
    const folders = await initializeFolderStructure();
    if (!folders?.progress) return;

    const fileName = `journey_${userId}_${date}.json`;

    // Check if today's file already exists
    const listRes = await window.gapi.client.drive.files.list({
      q: `name='${fileName}' and '${folders.progress}' in parents and trashed=false`,
      fields: 'files(id)',
      spaces: 'drive',
    });

    const existingFiles = listRes.result?.files || [];

    if (existingFiles.length > 0) {
      // File exists — read, append, update
      const fileId = existingFiles[0].id;
      let journeyData = { userId, date, lastUpdated: new Date().toISOString(), events: [] };

      try {
        const readRes = await window.gapi.client.drive.files.get({
          fileId,
          alt: 'media',
        });
        journeyData = JSON.parse(readRes.body);
      } catch {
        // If read fails, start fresh
      }

      journeyData.events = [...(journeyData.events || []), event];
      journeyData.lastUpdated = new Date().toISOString();

      const blob = new Blob(
        [JSON.stringify(journeyData, null, 2)],
        { type: 'application/json' }
      );

      const token = window.gapi.auth.getToken()?.access_token;
      if (!token) return;

      await fetch(
        `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
          body: blob,
        }
      );
    } else {
      // File doesn't exist — create new
      const journeyData = {
        userId,
        date,
        lastUpdated: new Date().toISOString(),
        events: [event],
      };
      const blob = new Blob(
        [JSON.stringify(journeyData, null, 2)],
        { type: 'application/json' }
      );
      await uploadFile(blob, folders.progress, fileName, 'application/json');
    }
  } catch {
    // Drive write failures are silent — Firestore remains source of truth
  }
}

// ─────────────────────────────────────────────
// 3. QUERYING EVENTS
// ─────────────────────────────────────────────

/**
 * Fetch journey events for the last `days` days from Firestore.
 * Returns newest first.
 */
export const getJourneyEvents = async (userId, days = 30) => {
  if (!userId) return [];

  const since = new Date();
  since.setDate(since.getDate() - days);

  const normalize = (d) => ({
    id: d.id,
    ...d.data(),
    ts: d.data().timestamp?.toDate?.() || new Date(),
  });

  // Try full composite-index query first
  try {
    const q = query(
      collection(db, 'journeyEvents'),
      where('userId', '==', userId),
      where('timestamp', '>=', Timestamp.fromDate(since)),
      orderBy('timestamp', 'desc'),
      limit(500)
    );
    const snap = await getDocs(q);
    return snap.docs.map(normalize);
  } catch (err) {
    // If index isn't ready yet, fall back to a simpler userId-only query
    if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
      try {
        const q2 = query(
          collection(db, 'journeyEvents'),
          where('userId', '==', userId),
          limit(500)
        );
        const snap2 = await getDocs(q2);
        const evs = snap2.docs.map(normalize);
        // Client-side date filter + sort
        return evs
          .filter(e => e.ts >= since)
          .sort((a, b) => b.ts - a.ts);
      } catch (err2) {
        console.error('getJourneyEvents fallback error:', err2);
        return [];
      }
    }
    console.error('getJourneyEvents error:', err);
    return [];
  }
};

/**
 * Aggregate events into a daily summary map:
 * { 'YYYY-MM-DD': { count, aiQueries, hubs: Set<string>, pdfs: Set<string> } }
 */
export const getDailyActivity = (events) => {
  const map = {};
  for (const ev of events) {
    const date = ev.date || ev.ts?.toISOString?.().slice(0, 10) || '';
    if (!date) continue;
    if (!map[date]) map[date] = { count: 0, aiQueries: 0, hubs: new Set(), pdfs: new Set() };
    map[date].count++;
    if (ev.type === 'ai_query') map[date].aiQueries++;
    if (ev.hubName) map[date].hubs.add(ev.hubName);
    if (ev.pdfName) map[date].pdfs.add(ev.pdfName);
  }
  return map;
};

/**
 * Return top N hubs by visit count.
 * Returns [{ name, count }, ...]
 */
export const getTopHubs = (events, n = 5) => {
  const counts = {};
  for (const ev of events) {
    if (ev.hubName) counts[ev.hubName] = (counts[ev.hubName] || 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, count]) => ({ name, count }));
};

/**
 * Return hour-of-day distribution for events.
 * Returns array of 24 numbers (counts per hour).
 */
export const getHourDistribution = (events) => {
  const hours = Array(24).fill(0);
  for (const ev of events) {
    if (ev.ts instanceof Date) hours[ev.ts.getHours()]++;
  }
  return hours;
};

// ─────────────────────────────────────────────
// 4. BLOOM'S TAXONOMY ANALYSIS (AI-powered)
// ─────────────────────────────────────────────

const BLOOMS_LEVELS = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];

/**
 * Classify AI query events by Bloom's Taxonomy using Gemini.
 * Checks a Drive cache file first (valid for the current day).
 *
 * @param {string} userId
 * @param {Array}  queryEvents — events with type === 'ai_query'
 * @returns {{ Remember: number, Understand: number, Apply: number, Analyze: number, Evaluate: number, Create: number }}
 */
export const analyzeBloomsLevel = async (userId, queryEvents) => {
  const empty = { Remember: 0, Understand: 0, Apply: 0, Analyze: 0, Evaluate: 0, Create: 0 };
  if (!queryEvents?.length) return empty;

  const today = new Date().toISOString().slice(0, 10);
  const cacheFileName = `journey_blooms_${userId}_${today}.json`;

  // Try Drive cache
  if (hasDrivePermissions()) {
    try {
      const folders = await initializeFolderStructure();
      if (folders?.progress) {
        const listRes = await window.gapi.client.drive.files.list({
          q: `name='${cacheFileName}' and '${folders.progress}' in parents and trashed=false`,
          fields: 'files(id)',
          spaces: 'drive',
        });
        if (listRes.result?.files?.length > 0) {
          const fileId = listRes.result.files[0].id;
          const readRes = await window.gapi.client.drive.files.get({ fileId, alt: 'media' });
          const cached = JSON.parse(readRes.body);
          if (cached?.counts) return cached.counts;
        }
      }
    } catch { /* cache miss — proceed to AI analysis */ }
  }

  // AI classification
  const texts = queryEvents
    .map(e => e.queryText || e.mode || '')
    .filter(Boolean)
    .slice(0, 50); // max 50 queries per analysis

  const prompt =
    `You are a learning analyst. Classify each of the following student learning queries by Bloom's Taxonomy level.\n\n` +
    `Bloom's levels:\n` +
    `- Remember: recall, list, define, name, identify facts\n` +
    `- Understand: explain, describe, summarize, interpret, paraphrase\n` +
    `- Apply: use, solve, calculate, demonstrate, implement\n` +
    `- Analyze: compare, contrast, examine, differentiate, break down\n` +
    `- Evaluate: judge, justify, critique, assess, defend, decide\n` +
    `- Create: design, build, compose, plan, generate, synthesize\n\n` +
    `Queries:\n${JSON.stringify(texts)}\n\n` +
    `Respond ONLY with a valid JSON object (no markdown, no explanation):\n` +
    `{"Remember":0,"Understand":0,"Apply":0,"Analyze":0,"Evaluate":0,"Create":0}`;

  try {
    const raw = await callLLM(prompt, {
      feature: 'general',
      preferredProvider: 'gemini',
      maxTokens: 256,
      temperature: 0.1,
    });

    // Extract JSON from response
    const match = raw.match(/\{[\s\S]*?\}/);
    if (!match) return empty;
    const counts = JSON.parse(match[0]);

    // Validate keys
    for (const level of BLOOMS_LEVELS) {
      if (typeof counts[level] !== 'number') counts[level] = 0;
    }

    // Save to Drive cache (fire-and-forget)
    if (hasDrivePermissions()) {
      initializeFolderStructure().then(folders => {
        if (!folders?.progress) return;
        const blob = new Blob(
          [JSON.stringify({ date: today, counts }, null, 2)],
          { type: 'application/json' }
        );
        uploadFile(blob, folders.progress, cacheFileName, 'application/json').catch(() => {});
      }).catch(() => {});
    }

    return counts;
  } catch {
    return empty;
  }
};

// ─────────────────────────────────────────────
// 5. AI INSIGHTS GENERATION
// ─────────────────────────────────────────────

/**
 * Generate personalized learning insights using Gemini.
 *
 * @param {Array}  events       — raw journey events (last 30 days)
 * @param {object} bloomsCounts — output of analyzeBloomsLevel()
 * @returns {{ strengths: string[], improvements: string[], habits: string[], overall: string }}
 */
export const generateInsights = async (events, bloomsCounts = {}) => {
  const fallback = { strengths: [], improvements: [], habits: [], overall: '' };
  if (!events?.length) return fallback;

  const daily = getDailyActivity(events);
  const activeDays = Object.keys(daily).length;
  const totalEvents = events.length;
  const aiQueryCount = events.filter(e => e.type === 'ai_query').length;
  const hubVisits = events.filter(e => e.type === 'hub_visit').length;
  const topHubs = getTopHubs(events, 3).map(h => h.name);
  const hours = getHourDistribution(events);
  const peakHour = hours.indexOf(Math.max(...hours));

  const modeCounts = {};
  events.filter(e => e.type === 'ai_query' && e.mode).forEach(e => {
    modeCounts[e.mode] = (modeCounts[e.mode] || 0) + 1;
  });
  const topMode = Object.entries(modeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Explain';

  const summary = {
    activeDays,
    totalEvents,
    aiQueryCount,
    hubVisits,
    topHubs,
    peakHour: `${peakHour}:00–${peakHour + 1}:00`,
    topChatMode: topMode,
    bloomsUsed: Object.entries(bloomsCounts)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k),
    bloomsMissing: ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create']
      .filter(l => !bloomsCounts[l] || bloomsCounts[l] === 0),
  };

  const prompt =
    `You are an expert learning coach. A student has shared their 30-day learning data. Provide personalized insights.\n\n` +
    `Data:\n${JSON.stringify(summary, null, 2)}\n\n` +
    `Bloom's Taxonomy scores: ${JSON.stringify(bloomsCounts)}\n\n` +
    `Respond ONLY with valid JSON (no markdown):\n` +
    `{\n` +
    `  "strengths": ["<2-3 specific strengths based on data>"],\n` +
    `  "improvements": ["<2-3 specific improvement suggestions>"],\n` +
    `  "habits": ["<2-3 study habit observations like peak time, frequency, preferred mode>"],\n` +
    `  "overall": "<2-sentence encouraging overall assessment>"\n` +
    `}`;

  try {
    const raw = await callLLM(prompt, {
      feature: 'general',
      preferredProvider: 'gemini',
      maxTokens: 512,
      temperature: 0.4,
    });
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return fallback;
    return JSON.parse(match[0]);
  } catch {
    return fallback;
  }
};

// ─────────────────────────────────────────────
// 6. CHAT HISTORY → GOOGLE DRIVE
// ─────────────────────────────────────────────

/**
 * Save the current hub chat messages to Google Drive as an HTML file.
 * Stored in Ekamanam/Notes/chat_{hubId}_{date}.html
 * Fire-and-forget — never blocks the UI.
 *
 * @param {string} hubId
 * @param {string} hubName
 * @param {Array}  messages  — [{role, content, displayContent}]
 */
export const saveChatHistoryToDrive = (hubId, hubName, messages) => {
  if (!hubId || !messages?.length) return;
  _saveChatHistory(hubId, hubName, messages).catch(() => {});
};

async function _saveChatHistory(hubId, hubName, messages) {
  if (!hasDrivePermissions()) return;
  try {
    const folders = await initializeFolderStructure();
    if (!folders?.notes) return;

    const today = new Date().toISOString().slice(0, 10);
    const fileName = `chat_${hubId}_${today}.html`;

    const rows = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => {
        const who = m.role === 'user' ? '<strong>You</strong>' : '<strong>Vyonn AI</strong>';
        const text = (m.displayContent || m.content || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
        const bg = m.role === 'user' ? '#e8f4ff' : '#f9f9f9';
        return `<div style="margin-bottom:12px;padding:10px 14px;background:${bg};border-radius:8px;">
          <div style="font-size:0.75em;color:#888;margin-bottom:4px;">${who}</div>
          <div style="font-size:0.9em;line-height:1.5;">${text}</div>
        </div>`;
      })
      .join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Vyonn Chat – ${hubName} – ${today}</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;max-width:800px;margin:0 auto}</style>
      </head><body>
      <h2>📖 Hub Chat: ${hubName}</h2>
      <p style="color:#888;font-size:0.85em;">Saved: ${new Date().toLocaleString()}</p>
      <hr>${rows}</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });

    // Update existing file or create new
    const listRes = await window.gapi.client.drive.files.list({
      q: `name='${fileName}' and '${folders.notes}' in parents and trashed=false`,
      fields: 'files(id)',
      spaces: 'drive',
    });
    const existing = listRes.result?.files || [];
    if (existing.length > 0) {
      const token = window.gapi.auth.getToken()?.access_token;
      if (!token) return;
      await fetch(
        `https://www.googleapis.com/upload/drive/v3/files/${existing[0].id}?uploadType=media`,
        { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/html' }, body: blob }
      );
    } else {
      await uploadFile(blob, folders.notes, fileName, 'text/html');
    }
  } catch {
    // Drive write failures are silent
  }
}

// ─────────────────────────────────────────────
// 7. RESOURCE JOURNEY LOGGING
// ─────────────────────────────────────────────

/**
 * Log that a resource (audio, slides, lab) was generated or restored from cache.
 * Fire-and-forget — never blocks UI.
 *
 * @param {string} userId
 * @param {object} resourceData — { type, key, pdfId, pdfName, hubId?, hubName?, bloomsLevels, fromCache }
 */
export const logResourceGenerated = (userId, resourceData) => {
  logJourneyEvent(userId, 'resource_generated', resourceData);
};

/**
 * Log that a resource was completed (audio finished, all slides viewed, lab scored).
 * Writes to Firestore journeyEvents and mirrors to Drive.
 *
 * @param {string} userId
 * @param {object} resourceData — { type, key, pdfId?, pdfName?, bloomsLevels, score?, total?, pct? }
 */
export const logResourceCompleted = (userId, resourceData) => {
  if (!userId) return;
  const today = new Date().toISOString().slice(0, 10);
  const eventData = { ...resourceData, completedAt: new Date().toISOString() };

  addDoc(collection(db, 'journeyEvents'), {
    userId, type: 'resource_completed', timestamp: serverTimestamp(), date: today, ...eventData,
  }).catch(() => {});

  _appendEventToDrive(userId, today, { type: 'resource_completed', date: today, ...eventData })
    .catch(() => {});
};

// ─────────────────────────────────────────────
// 8. QUIZ RESULT LOGGING
// ─────────────────────────────────────────────

/**
 * Log a completed practice quiz result to Firestore (journeyEvents).
 * Also fires a plain journeyEvent of type 'quiz_complete' for the activity log.
 *
 * @param {string} userId
 * @param {object} quizData — { hubId, hubName, pdfId, pdfName, score, total, pct, difficulty, byBlooms, byDOK, byType, perQuestion }
 */
export const logQuizResult = async (userId, quizData = {}) => {
  if (!userId) return;
  const today = new Date().toISOString().slice(0, 10);

  // Full quiz data stored as a journey event — more data = better tracking
  const perQuestion = (quizData.perQuestion || []).map(pq => ({
    question: (pq.question || '').substring(0, 200),
    type: pq.type || '',
    bloomsLevel: pq.bloomsLevel || '',
    dok: pq.dok || 1,
    correct: pq.correct ?? null,
    feedback: (pq.feedback || '').substring(0, 300),
  }));

  const event = {
    userId,
    type: 'quiz_complete',
    timestamp: serverTimestamp(),
    date: today,
    hubId:    quizData.hubId    || '',
    hubName:  quizData.hubName  || '',
    pdfId:    quizData.pdfId    || '',
    pdfName:  quizData.pdfName  || '',
    score:    quizData.score    ?? 0,
    total:    quizData.total    ?? 0,
    pct:      quizData.pct      ?? 0,
    difficulty: quizData.difficulty || 'Mixed',
    byBlooms: quizData.byBlooms || {},
    byDOK:    quizData.byDOK    || {},
    byType:   quizData.byType   || {},
    perQuestion,
  };

  await addDoc(collection(db, 'journeyEvents'), event).catch(() => {});
};
